import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Crown, Star, ShieldCheck, Clock, Headphones, TrendingUp, Target, Zap, ChevronDown, Trophy, BadgeCheck, Ban } from 'lucide-react';
import Steps from '../components/Steps';
import Reviews from '../components/Reviews';
import { pricingPlans } from '../mock';
import { fetchSettings } from '../api';
import Seo from '../components/Seo';

const included = [
  { icon: Target, title: 'One Premium Tip Daily', desc: 'A single, carefully selected match every day with odds between 2.00 and 4.00.' },
  { icon: TrendingUp, title: 'Full Statistical Breakdown', desc: 'Form, xG, team news and market movement — the reasoning behind every pick.' },
  { icon: Clock, title: 'Delivered Before Kickoff', desc: 'Tips land in your inbox in good time, every single day of your subscription.' },
  { icon: Headphones, title: 'Priority Email Support', desc: 'A responsive team available 24/7 to answer any question you have.' },
  { icon: ShieldCheck, title: 'Transparent Track Record', desc: 'Every result — win or loss — is published openly in our public archive.' },
  { icon: Zap, title: 'Disciplined Value Betting', desc: 'We only release a selection when confidence and value are genuinely optimal.' },
];

const compareRows = [
  ['Daily premium single tip', true, true],
  ['Detailed reasoning per tip', true, true],
  ['Full statistical breakdown', true, true],
  ['Priority email support', true, true],
  ['VIP priority support', false, true],
  ['Free replacement on rare loss', false, true],
];

const faqs = [
  { q: 'How do I receive the tips?', a: 'Once your subscription is confirmed, our team delivers each daily tip to the email address you provide, in good time before the match kicks off.' },
  { q: 'Are the predictions guaranteed to win?', a: 'No. Our tips are expert predictions backed by deep analysis, not guarantees. No genuine service can guarantee results. We focus on long-term, disciplined value betting.' },
  { q: 'How do I subscribe?', a: 'Simply send us a message through the contact page selecting your preferred plan. Our team will reply promptly with everything you need to get started.' },
  { q: 'Can I see your past results?', a: 'Absolutely. Our entire history of tips — wins and losses — is published openly in the Full Archive, with an automatically calculated win rate.' },
  { q: 'What is the difference between the plans?', a: 'Both plans deliver one premium tip per day. The 30 Daily Tips plan adds VIP priority support and a free replacement tip on any rare loss.' },
];

function FaqItem({ item, open, onToggle }) {
  return (
    <div className="card-dark rounded-xl">
      <button onClick={onToggle} className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left">
        <span className="font-heading text-lg font-bold text-white">{item.q}</span>
        <ChevronDown className={`h-5 w-5 flex-shrink-0 text-orange-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-6 pb-5 text-sm leading-relaxed text-neutral-400">{item.a}</div>}
    </div>
  );
}

export default function Pricing() {
  const [openFaq, setOpenFaq] = useState(0);
  const [weeklyAvailable, setWeeklyAvailable] = useState(true);
  const [plans, setPlans] = useState(pricingPlans);

  useEffect(() => {
    (async () => {
      try {
        const s = await fetchSettings();
        setWeeklyAvailable(s.weeklyAvailable !== false);
        if (Array.isArray(s.plans) && s.plans.length) setPlans(s.plans);
      } catch (e) { /* default available + mock plans */ }
    })();
  }, []);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: '24BetBox VIP Football Predictions',
      description: 'Premium daily football predictions delivered by verified professional tipsters.',
      brand: { '@type': 'Brand', name: '24BetBox' },
      offers: pricingPlans.map((p) => ({
        '@type': 'Offer',
        name: p.name,
        price: p.price,
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        url: 'https://24betbox.com/pricing',
      })),
    },
  ];

  return (
    <div>
      <Seo
        title="Plans & Pricing | VIP Football Tips Subscription – 24BetBox"
        description="Choose your 24BetBox VIP football tips plan. Premium daily predictions from €119. Transparent pricing, verified tipsters, and a fully published results archive."
        path="/pricing"
        jsonLd={jsonLd}
      />
      {/* Hero */}
      <section className="hero-gradient border-b border-[#2e2822]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
          <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">Plans & Pricing</p>
          <h1 className="mt-2 font-heading text-5xl font-extrabold text-white sm:text-6xl">Choose Your Winning Plan</h1>
          <p className="mt-4 max-w-2xl text-neutral-300">Premium daily football predictions from a team of verified professional tipsters. Simple, transparent pricing — no hidden fees, no buy buttons, just results you can track.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#2e2822] bg-white/5 px-3 py-1.5 text-xs font-semibold text-neutral-300"><BadgeCheck className="h-4 w-4 text-orange-500" /> Verified tipsters</span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#2e2822] bg-white/5 px-3 py-1.5 text-xs font-semibold text-neutral-300"><ShieldCheck className="h-4 w-4 text-orange-500" /> Transparent archive</span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#2e2822] bg-white/5 px-3 py-1.5 text-xs font-semibold text-neutral-300"><Headphones className="h-4 w-4 text-orange-500" /> 24/7 support</span>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="bg-[#15120f] py-14">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          {/* Limited VIP offer */}
          <div className="mb-8 overflow-hidden rounded-2xl border border-orange-500/40 bg-gradient-to-r from-[#241a10] via-[#1f1913] to-[#1b1712]" data-testid="vip-100day-offer">
            <div className="flex flex-col items-center gap-4 p-5 sm:flex-row sm:justify-between sm:gap-6 sm:p-6">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-orange-500/15">
                  <Zap className="h-7 w-7 text-orange-500" />
                </div>
                <div className="text-center sm:text-left">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500 px-3 py-0.5 font-heading text-[11px] font-extrabold uppercase tracking-wide text-[#15120f]">Limited VIP Offer · 35% OFF</span>
                  <h3 className="mt-1.5 font-heading text-2xl font-extrabold text-white">100-Day Subscription</h3>
                  <p className="text-sm text-neutral-400">Get maximum value with our longest premium package — expert daily tips for 100 days.</p>
                </div>
              </div>
              <div className="flex items-center gap-5">
                <div className="text-center">
                  <p className="text-sm text-neutral-500 line-through">€1285</p>
                  <p className="font-heading text-4xl font-extrabold orange-text-gradient leading-none">€835</p>
                  <p className="text-xs text-neutral-500">100 days</p>
                </div>
                <Link to="/contact" className="btn-orange inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-6 py-3 font-heading text-sm font-bold uppercase tracking-wide">Claim This Offer</Link>
              </div>
            </div>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {plans.map((p) => {
              const unavailable = p.id === 'weekly' && !weeklyAvailable;
              return (
              <div key={p.id} data-testid={`plan-card-${p.id}`} className={`card-dark relative flex flex-col rounded-2xl p-8 ${p.highlight ? 'ring-2 ring-orange-500' : ''} ${unavailable ? 'opacity-60 grayscale' : ''}`}>
                {unavailable ? (
                  <span data-testid="plan-not-available-badge" className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-red-500 px-4 py-1 font-heading text-xs font-bold uppercase tracking-wide text-white"><Ban className="h-3.5 w-3.5" /> Not Available</span>
                ) : (
                  <span className={`absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-4 py-1 font-heading text-xs font-bold uppercase tracking-wide ${p.highlight ? 'bg-orange-500 text-[#15120f]' : 'bg-[#2e2822] text-neutral-300'}`}>{p.badge}</span>
                )}
                <div className="flex items-center gap-2">
                  {p.highlight ? <Crown className="h-6 w-6 text-orange-500" /> : <Star className="h-6 w-6 text-orange-500" />}
                  <h3 className="font-heading text-2xl font-extrabold text-white">{p.name}</h3>
                </div>
                <p className="mt-2 text-sm text-neutral-400">{p.subtitle}</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="font-heading text-5xl font-extrabold orange-text-gradient">€{p.price}</span>
                  <span className="mb-2 text-sm text-neutral-500">/ {p.period}</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-neutral-300">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {unavailable ? (
                  <button disabled data-testid="plan-unavailable-btn" className="mt-8 w-full cursor-not-allowed rounded-lg border border-[#3a322a] bg-white/5 py-3.5 text-center font-heading text-base font-bold uppercase tracking-wide text-neutral-500">Not Available</button>
                ) : (
                  <Link to="/contact" className="btn-orange mt-8 w-full rounded-lg py-3.5 text-center font-heading text-base font-bold uppercase tracking-wide">Request This Plan</Link>
                )}
              </div>
              );
            })}
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-center text-xs text-neutral-500">To subscribe, contact us through the form and our team will reply with the next steps. Prices are in Euro (€).</p>
        </div>
      </section>

      {/* What's included */}
      <section className="section-fade py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="text-center">
            <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">Every Plan Includes</p>
            <h2 className="mt-2 font-heading text-4xl font-extrabold text-white sm:text-5xl">Premium Service, End To End</h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {included.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="card-dark rounded-xl p-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/15"><Icon className="h-6 w-6 text-orange-500" /></div>
                  <h3 className="mt-4 font-heading text-lg font-bold text-white">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-400">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="bg-[#15120f] py-14">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="text-center">
            <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">Compare Plans</p>
            <h2 className="mt-2 font-heading text-4xl font-extrabold text-white sm:text-5xl">Find The Right Fit</h2>
          </div>
          <div className="mt-8 overflow-hidden rounded-xl border border-[#2e2822] bg-[#1b1712]">
            <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-left text-sm">
              <thead>
                <tr className="bg-[#241e18] text-neutral-300">
                  <th className="px-5 py-4 font-heading text-base font-bold">Feature</th>
                  <th className="px-5 py-4 text-center font-heading text-base font-bold">7 Daily Tips · €119</th>
                  <th className="px-5 py-4 text-center font-heading text-base font-bold text-orange-400">30 Daily Tips · €399</th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map(([label, a, b], i) => (
                  <tr key={i} className="border-t border-[#2e2822]">
                    <td className="px-5 py-3.5 text-neutral-300">{label}</td>
                    <td className="px-5 py-3.5 text-center">{a ? <Check className="mx-auto h-5 w-5 text-emerald-400" /> : <span className="text-neutral-600">—</span>}</td>
                    <td className="px-5 py-3.5 text-center">{b ? <Check className="mx-auto h-5 w-5 text-emerald-400" /> : <span className="text-neutral-600">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      </section>

      <Steps />

      {/* FAQ */}
      <section className="section-fade py-14">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="text-center">
            <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">FAQ</p>
            <h2 className="mt-2 font-heading text-4xl font-extrabold text-white sm:text-5xl">Frequently Asked Questions</h2>
          </div>
          <div className="mt-8 space-y-3">
            {faqs.map((item, i) => (
              <FaqItem key={i} item={item} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      <Reviews />

      {/* CTA */}
      <section className="bg-[#15120f] py-14">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="card-dark relative overflow-hidden rounded-2xl p-10 text-center">
            <div className="absolute left-1/2 top-0 h-40 w-80 -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl" />
            <Trophy className="relative mx-auto h-12 w-12 text-orange-500" />
            <h2 className="relative mt-4 font-heading text-4xl font-extrabold text-white">Ready To Get Started?</h2>
            <p className="relative mx-auto mt-3 max-w-xl text-neutral-300">Pick your plan and contact our team today — your first premium tip could be on its way tomorrow.</p>
            <Link to="/contact" className="btn-orange relative mt-8 inline-flex items-center gap-2 rounded-lg px-8 py-4 font-heading text-lg font-bold uppercase tracking-wide"><Crown className="h-5 w-5" /> Request A Plan</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
